import { render } from "preact";

import { App } from "./App";
import { CSS } from "./styles";

/**
 * Widget entry point.
 *
 * Mounts into a Shadow DOM so the host page can't restyle us and we can't leak
 * styles into it — the same embed shape the incumbent uses, which means this can
 * be dropped onto the practice's site next to DrTalks for a live A/B.
 *
 *   <script defer src="https://.../vm-agent.js" data-api-base="https://..."
 *           data-label="Ask a question"></script>
 */

const MOUNT_ID = "vm-agent-widget";

function embedScript(): HTMLScriptElement | null {
  return (
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>('script[src*="vm-agent.js"]')
  );
}

/** `data-label` on the script tag, for practices that want their own wording. */
function launcherLabel(): string | undefined {
  return embedScript()?.dataset.label?.trim() || undefined;
}

function apiBase(): string {
  const script = embedScript();

  const explicit = script?.dataset.apiBase;
  if (explicit) return explicit.replace(/\/$/, "");

  // Default to wherever the script was served from.
  if (script?.src) {
    try {
      return new URL(script.src).origin;
    } catch {
      /* fall through */
    }
  }
  return "";
}

function mount(): void {
  if (document.getElementById(MOUNT_ID)) return;

  const host = document.createElement("div");
  host.id = MOUNT_ID;
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = CSS;
  shadow.appendChild(style);

  const container = document.createElement("div");
  shadow.appendChild(container);

  render(<App apiBase={apiBase()} launcherLabel={launcherLabel()} />, container);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mount, { once: true });
} else {
  mount();
}
